import type {CompanyMessageCountsSerializer} from "@/generated/DjangoTypes";
import {useUserPermission} from "@/hooks/useUserHooks";
import {defineAbilityFor} from "@/permissions";
import {create} from "@/store/createStore";
import type {TNonFunctionKeys} from "@/store/types";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TState = {
  isOpen: boolean;
  selectedTab: "notifications" | "chat";
  toggleOpen(isOpen?: boolean): void;
  notificationCounts: CompanyMessageCountsSerializer;
  onChatOpened(params: {unreadCount: number}): void;
  resetNotificationCounts(): void;
  setValue<K extends TNonFunctionKeys<TState>>(key: K, value: TState[K]): void;
};

const initialState = {
  isOpen: false,
  selectedTab: "chat",
  notificationCounts: {
    notification: {seen: 0, unseen: 0},
    chat: {unread: 0, needs_reply: 0},
  },
} as TState;

const useNotificationStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "useNotificationStore/setValue",
          ),
        toggleOpen: (isOpen = !get().isOpen) => {
          const userPermission = useUserPermission();
          const ability = defineAbilityFor(userPermission);

          set(
            state => {
              state.isOpen = isOpen;
              state.selectedTab =
                ability.cannot("list", "ChatMessages") ||
                (get().notificationCounts.chat?.unread === 0 && get().notificationCounts.notification.unseen !== 0)
                  ? "notifications"
                  : "chat";
            },
            false,
            "useNotificationStore/toggleOpen",
          );
        },
        resetNotificationCounts: () => {
          set(
            state => {
              state.notificationCounts.notification = {seen: 0, unseen: 0};
            },
            false,
            "useNotificationStore/resetNotificationCounts",
          );
        },
        onChatOpened: ({unreadCount}) => {
          set(
            state => {
              state.isOpen = false;
              state.notificationCounts.chat.unread = Math.max(0, state.notificationCounts.chat.unread - unreadCount);
            },
            false,
            "useNotificationStore/onChatOpened",
          );
        },
      }),
      {name: "useNotificationStore"},
    ),
  ),
);

export default useNotificationStore;
